// Discussion items table for a site visit (DO-2298 pivot / WP-H). Columns: # ·
// Priority · Topic · Notes · Ticket · Status. Accepted rows only are passed in
// from VisitDetail. Ticket cell: Raise for raise / raise-fr / road-map-fr.
import { useState } from "react";
import { useSnackbar } from "notistack";
import { DataTable, TextField, SelectField, Pill, Button, type Column } from "../../design/ui";
import {
  createDiscussionItem, patchDiscussionItem,
  type DiscussionItem, type ItemPriority, type ItemStatus, type RaiseTarget,
} from "../../lib/brain-api";
import { RaiseTicketDialog } from "./RaiseTicketDialog";

const PRIORITY_OPTIONS: { value: ItemPriority | ""; label: string }[] = [
  { value: "", label: "—" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];
const STATUS_OPTIONS: { value: ItemStatus; label: string }[] = [
  { value: "open", label: "Open" },
  { value: "investigate", label: "Investigate" },
  { value: "with-client", label: "With client" },
  { value: "needs-discussion", label: "Needs Discussion" },
  { value: "in-ticket", label: "In ticket" },
  { value: "done", label: "Done" },
  { value: "dropped", label: "Dropped" },
];
const DISPOSITION_LABEL: Record<string, string> = {
  raise: "Raise",
  "raise-fr": "Raise FR",
  "road-map-fr": "Road map FR",
  discuss: "Discuss",
  linked: "Linked",
  resolved: "Resolved",
};
/** disposition → the RaiseTicketDialog target it pre-selects. */
const RAISE_DEFAULT_TARGET: Record<string, RaiseTarget> = {
  raise: "support",
  "raise-fr": "feature-request",
  "road-map-fr": "feature-request",
  discuss: "support",
};
const RAISEABLE = new Set(["raise", "raise-fr", "road-map-fr"]);
const JIRA_BROWSE_BASE = "https://aeronetnz.atlassian.net/browse";

export function ItemsTable({ customerId, visitId, items, onChanged, visitAudited }: {
  customerId: string;
  visitId: string;
  items: DiscussionItem[];
  onChanged: () => void;
  visitAudited?: boolean;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [newTopic, setNewTopic] = useState("");
  const [adding, setAdding] = useState(false);
  const [raising, setRaising] = useState<{ item: DiscussionItem; target: RaiseTarget } | null>(null);

  const patch = async (
    id: number,
    p: Partial<Pick<DiscussionItem, "topic" | "notes" | "priority" | "status">>
  ) => {
    try {
      await patchDiscussionItem(id, p);
      onChanged();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Couldn't save — try again", { variant: "error" });
    }
  };

  const addRow = async () => {
    if (!newTopic.trim()) return;
    setAdding(true);
    try {
      await createDiscussionItem({ customerId, visitId, topic: newTopic.trim() });
      setNewTopic("");
      onChanged();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Couldn't add item — try again", { variant: "error" });
    } finally {
      setAdding(false);
    }
  };

  const columns: Column<DiscussionItem>[] = [
    { key: "n", header: "#", width: 44, align: "center", render: (row) => row.n, sortValue: (row) => row.n },
    {
      key: "priority",
      header: "Priority",
      width: 110,
      render: (row) => (
        <SelectField
          value={row.priority ?? ""}
          onChange={(e) => void patch(row.id, { priority: (e.target.value || null) as ItemPriority | null })}
        >
          {PRIORITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </SelectField>
      ),
    },
    {
      key: "topic",
      header: "Topic",
      render: (row) => (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {row.createdBy === "ai" && !visitAudited && !row.audited && <Pill icon="robot">AI</Pill>}
          <TextField
            defaultValue={row.topic}
            onBlur={(e) => {
              const v = e.target.value.trim();
              if (v && v !== row.topic) void patch(row.id, { topic: v });
            }}
          />
        </div>
      ),
    },
    {
      key: "notes",
      header: "Notes",
      render: (row) => (
        <TextField
          defaultValue={row.notes ?? ""}
          onBlur={(e) => {
            const v = e.target.value;
            if (v !== (row.notes ?? "")) void patch(row.id, { notes: v || null });
          }}
        />
      ),
    },
    {
      key: "ticket",
      header: "Ticket",
      width: 150,
      render: (row) => {
        if (row.jiraKey) {
          return (
            <a
              href={`${JIRA_BROWSE_BASE}/${row.jiraKey}`}
              target="_blank"
              rel="noreferrer"
              title="Open in Jira"
              style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--kt-teal-ink)", textDecoration: "none" }}
            >
              {row.jiraKey}
            </a>
          );
        }
        if (RAISEABLE.has(row.disposition)) {
          return (
            <Button
              size="sm"
              icon="ticket"
              onClick={() => setRaising({ item: row, target: RAISE_DEFAULT_TARGET[row.disposition] ?? "support" })}
            >
              Raise
            </Button>
          );
        }
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 12, color: "var(--kt-text-muted)" }}>
              {DISPOSITION_LABEL[row.disposition] ?? row.disposition}
            </span>
            {row.disposition === "discuss" && (
              <button
                className="kt-menu-item"
                style={{ width: "auto", padding: "2px 6px", fontSize: 11, color: "var(--kt-text-muted)" }}
                title="Raise a ticket for this item anyway"
                onClick={() => setRaising({ item: row, target: RAISE_DEFAULT_TARGET.discuss })}
              >
                Raise anyway…
              </button>
            )}
          </div>
        );
      },
    },
    {
      key: "status",
      header: "Status",
      width: 150,
      render: (row) => (
        <SelectField value={row.status} onChange={(e) => void patch(row.id, { status: e.target.value as ItemStatus })}>
          {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </SelectField>
      ),
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <DataTable columns={columns} rows={items} getRowId={(row) => row.id} empty="No discussion items yet." />
      <div style={{ display: "flex", gap: 8 }}>
        <TextField
          placeholder="New discussion item…"
          value={newTopic}
          onChange={(e) => setNewTopic(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") void addRow(); }}
          style={{ flex: 1 }}
        />
        <Button icon="plus" disabled={adding || !newTopic.trim()} onClick={() => void addRow()}>Add item</Button>
      </div>
      {raising && (
        <RaiseTicketDialog
          key={raising.item.id}
          customerId={customerId}
          visitId={visitId}
          subject={{ kind: "item", item: raising.item }}
          defaultTarget={raising.target}
          onClose={() => setRaising(null)}
          onChanged={onChanged}
        />
      )}
    </div>
  );
}
